/**
 * <ClickToAgree /> — keystone signature component.
 *
 * Route: /onboarding/sign/:itemId
 *
 * Behavior:
 *   1. Loads the checklist item and linked policy_document.
 *   2. Renders the policy body (markdown -> sanitized HTML).
 *   3. Provides a "Download PDF" link to the canonical policy PDF.
 *   4. Tracks scroll-to-bottom of the policy body with a percent meter.
 *   5. Submit is disabled until: scrolled to bottom AND name typed
 *      AND checkbox checked AND name matches employee.legal_name
 *      (case-insensitive, whitespace-normalized).
 *   6. On submit, calls createSignature Cloud Function which emails the
 *      signed PDF to HR and marks the checklist item complete. Shows a
 *      confirmation screen with a "Continue to next item" CTA when more
 *      work remains.
 *   7. On submit failure, distinguishes offline from generic errors so
 *      users on flaky cell connections can retry without re-typing.
 *
 * @prop {string} itemId  taken from route params; the checklist_items doc id.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Download, CheckCircle2, ArrowLeft, ArrowRight, WifiOff, RefreshCw } from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { useAuth } from '../../../context/AuthContext';
import { useApp } from '../../../context/AppContext';
import {
  getChecklistItem,
  getPolicyDocument,
  ensurePolicyDocument,
  createSignature,
  listChecklistItems,
} from '../services/onboardingService';
import { renderMarkdown } from '../utils/renderMarkdown';
import '../Onboarding.css';

function buildLegalName(emp) {
  if (!emp) return '';
  if (emp.legal_name) return String(emp.legal_name).trim();
  return [emp.firstName, emp.lastName].filter(Boolean).join(' ').trim();
}

function normalizeName(s) {
  return String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function isOfflineError(err) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (err && err.name === 'TypeError') return true;
  const msg = String(err?.message || '').toLowerCase();
  return msg.includes('network') || msg.includes('failed to fetch');
}

export default function ClickToAgree() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { state } = useApp();

  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [scrollPercent, setScrollPercent] = useState(0);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState('');
  const [submitError, setSubmitError] = useState(null); // { offline: bool, message: string }
  const [done, setDone] = useState(null); // { signatureId, snapshotUrl }
  const [nextItemPath, setNextItemPath] = useState(null);
  const policyBodyRef = useRef(null);
  const confirmHeadingRef = useRef(null);

  const employee = user?.email
    ? state.employees.find((e) => e.email?.toLowerCase() === user.email.toLowerCase())
    : null;
  const legalName = buildLegalName(employee);
  const normalizedLegal = normalizeName(legalName);
  const normalizedTyped = normalizeName(typedName);
  const nameMatches = !!normalizedLegal && normalizedTyped === normalizedLegal;

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const it = await getChecklistItem(itemId);
        if (!active) return;
        if (!it) {
          toast.error('Checklist item not found');
          navigate('/onboarding');
          return;
        }
        setItem(it);
        if (it.status === 'completed' && it.metadata?.signature_id) {
          // Signed PDFs are emailed to HR rather than stored in Firestore.
          // Re-visiting a completed item just shows the confirmation state;
          // there's no downloadable snapshot URL anymore.
          setDone({ signatureId: it.metadata.signature_id, snapshotUrl: null });
        }
        if (it.policy_document_id) {
          let p = await getPolicyDocument(it.policy_document_id);
          if (!p || !p.body_markdown) {
            try {
              const repaired = await ensurePolicyDocument(it.policy_document_id);
              if (repaired) p = repaired;
            } catch (e) {
              console.warn('ensurePolicyDocument failed:', e.message);
            }
          }
          setPolicy(p);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [itemId, navigate, toast]);

  // Once a signature is confirmed, look up the next incomplete employee-driven
  // item so we can offer a one-tap "Continue" CTA instead of dropping the user
  // back at the checklist.
  useEffect(() => {
    if (!done || !employee?.id) return;
    let active = true;
    (async () => {
      try {
        const rows = await listChecklistItems(employee.id);
        if (!active) return;
        const next = rows.find(
          (r) => r.id !== itemId && r.driver !== 'manager' && r.status !== 'completed'
        );
        if (next) {
          const path = next.type === 'esign'
            ? `/onboarding/sign/${next.id}`
            : next.type === 'upload'
              ? `/onboarding/upload/${next.id}`
              : null;
          setNextItemPath(path);
        }
      } catch (_) {
        // Non-fatal: user can still tap "Back to checklist".
      }
    })();
    return () => { active = false; };
  }, [done, employee?.id, itemId]);

  // Move focus to the confirmation heading when the success screen renders.
  useEffect(() => {
    if (done && confirmHeadingRef.current) {
      confirmHeadingRef.current.focus();
    }
  }, [done]);

  const handleScroll = useCallback((e) => {
    const el = e.target;
    const max = el.scrollHeight - el.clientHeight;
    const pct = max > 0 ? Math.min(100, Math.round((el.scrollTop / max) * 100)) : 100;
    setScrollPercent(pct);
    // Bumped tolerance from 12 → 24 to handle iOS Safari rubber-band scrolling.
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) {
      setScrolledToBottom(true);
    }
  }, []);

  // Edge case: if the policy is short enough to not be scrollable, treat as
  // already-bottomed once it's rendered.
  useEffect(() => {
    if (!policyBodyRef.current || !policy) return;
    const el = policyBodyRef.current;
    if (el.scrollHeight <= el.clientHeight + 4) {
      setScrolledToBottom(true);
      setScrollPercent(100);
    }
  }, [policy]);

  const validateNameOnBlur = () => {
    if (!typedName) { setNameError(''); return; }
    if (normalizedLegal && !nameMatches) {
      setNameError(`Name must match the legal name on file (${legalName}).`);
    } else {
      setNameError('');
    }
  };

  const canSubmit =
    scrolledToBottom &&
    !!typedName.trim() &&
    agreed &&
    !submitting &&
    !nameError &&
    nameMatches;

  const doSubmit = async () => {
    if (!employee || !item || !policy) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await createSignature({
        employeeId: employee.id,
        policyDocumentId: policy.id,
        typedName: typedName.trim(),
        checklistItemId: item.id,
      });
      setDone(result);
      toast.success('Signature recorded');
    } catch (err) {
      if (isOfflineError(err)) {
        setSubmitError({
          offline: true,
          message: 'You appear to be offline. Your signature was not recorded.',
        });
      } else {
        setSubmitError({
          offline: false,
          message: err.message || 'Could not record signature. Please try again.',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    doSubmit();
  };

  if (loading) {
    return (
      <div className="onboarding-shell">
        <div className="loading-screen"><div className="spinner-large" /><p>Loading…</p></div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="onboarding-shell">
        <div className="onboarding-celebration">
          <CheckCircle2 size={56} color="#3b6e4a" aria-hidden="true" />
          <h2 ref={confirmHeadingRef} tabIndex={-1} style={{ outline: 'none' }}>Signed</h2>
          <p>Your acknowledgment of <strong>{item?.title}</strong> has been recorded and emailed to HR.</p>
          {nextItemPath ? (
            <>
              <button className="onboarding-btn" onClick={() => navigate(nextItemPath)}>
                Continue to next item <ArrowRight size={16} aria-hidden="true" style={{ marginLeft: 6 }} />
              </button>
              <div style={{ marginTop: 12 }}>
                <button className="onboarding-btn secondary" onClick={() => navigate('/onboarding')}>
                  Back to checklist
                </button>
              </div>
            </>
          ) : (
            <button className="onboarding-btn" onClick={() => navigate('/onboarding')}>
              Back to checklist
            </button>
          )}
        </div>
      </div>
    );
  }

  const showLiveHint =
    !nameError &&
    typedName.trim().length > 0 &&
    !nameMatches;

  return (
    <div className="onboarding-shell">
      <div className="onboarding-header">
        <Link to="/onboarding" className="onboarding-btn secondary" style={{ padding: '6px 10px', fontSize: '0.8rem' }}>
          <ArrowLeft size={14} aria-hidden="true" /> Back
        </Link>
        <h1 style={{ marginTop: 8 }}>{item?.title}</h1>
        {policy && (
          <div className="onboarding-progress-meta">
            <span>Version {policy.current_version}</span>
            {policy.pdf_url && (
              <a href={policy.pdf_url} target="_blank" rel="noreferrer">
                <Download size={14} aria-hidden="true" /> Download PDF
              </a>
            )}
          </div>
        )}
      </div>

      <div
        className="onboarding-policy-body"
        ref={policyBodyRef}
        onScroll={handleScroll}
        tabIndex={0}
        aria-label="Policy text — scroll to the bottom to continue"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(policy?.body_markdown || '') }}
      />

      {!scrolledToBottom && (
        <div className="onboarding-scroll-hint" aria-live="polite">
          <span>Scroll to the bottom of the policy to continue</span>
          <span className="onboarding-scroll-meter" aria-hidden="true">{scrollPercent}%</span>
        </div>
      )}

      <div className="onboarding-form-row">
        <label htmlFor="typed-name">Type your full legal name</label>
        <input
          id="typed-name"
          type="text"
          autoComplete="name"
          placeholder="e.g. Jane M. Doe"
          value={typedName}
          aria-describedby={nameError ? 'typed-name-error' : showLiveHint ? 'typed-name-hint' : undefined}
          aria-invalid={!!nameError}
          onChange={(e) => { setTypedName(e.target.value); setNameError(''); }}
          onBlur={validateNameOnBlur}
        />
        {nameError && <div id="typed-name-error" className="field-error">{nameError}</div>}
        {showLiveHint && (
          <div id="typed-name-hint" className="field-hint">
            Keep typing — must match {legalName}.
          </div>
        )}
      </div>

      <label className="onboarding-checkbox">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
        <span>I have read and agree to this policy. I understand that typing my name above is a legally binding electronic signature.</span>
      </label>

      {submitError && (
        <div className="onboarding-retry-banner" role="alert">
          {submitError.offline && <WifiOff size={18} aria-hidden="true" />}
          <div style={{ flex: 1 }}>{submitError.message}</div>
          <button
            type="button"
            className="onboarding-btn"
            onClick={doSubmit}
            disabled={submitting}
          >
            <RefreshCw size={14} aria-hidden="true" style={{ marginRight: 6 }} />
            {submitting ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}

      <button
        className="onboarding-btn"
        style={{ width: '100%', marginTop: 8 }}
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        {submitting ? 'Recording signature…' : 'Sign & continue'}
      </button>
    </div>
  );
}
